import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatLocation } from "../lib/cities";
import { PROVINCE_NAME, CATEGORIES } from "../lib/constants";
import { CATEGORY_LABELS_FR } from "../lib/i18n";
import { ListingCard } from "./ListingCard";
import { CityInput } from "./CityInput";
import { NEAR_CITY_EVENT, NEAR_RADIUS_EVENT } from "./LostNearYou";
import { ArrowRight, MapPin, Navigation, X } from "lucide-react";

export function NearYou({ onItems, exclude }) {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [cat, setCat] = useState("all");
  const pickKey = `cp_near_city_${user?.user_id || "anon"}`;
  const [picked, setPicked] = useState("");
  useEffect(() => { if (user !== null) setPicked(localStorage.getItem(pickKey) || ""); }, [pickKey, user]);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [cityText, setCityText] = useState("");
  const radKey = `cp_near_radius_${user?.user_id || "anon"}`;
  const [radius, setRadius] = useState(() => localStorage.getItem(radKey) || "province");
  useEffect(() => { setRadius(localStorage.getItem(radKey) || "province"); }, [radKey]);
  const chooseRadius = (v) => { localStorage.setItem(radKey, v); setRadius(v); window.dispatchEvent(new CustomEvent(NEAR_RADIUS_EVENT, { detail: v })); };
  const sortKey = `cp_near_sort_${user?.user_id || "anon"}`;
  const [sort, setSort] = useState(() => localStorage.getItem(sortKey) || "newest");
  useEffect(() => { setSort(localStorage.getItem(sortKey) || "newest"); }, [sortKey]);
  const chooseSort = (v) => { localStorage.setItem(sortKey, v); setSort(v); };

  useEffect(() => {
    if (user === null || picked !== (localStorage.getItem(pickKey) || "")) return;
    const qs = `?radius=${radius}&sort=${sort}${cat !== "all" ? `&category=${cat}` : ""}${picked ? `&city=${encodeURIComponent(picked)}` : ""}`;
    api.get(`/listings/nearby${qs}`).then((r) => setData(r.data)).catch(() => setData({ origin: null, items: [] }));
  }, [picked, user, pickKey, radius, sort, cat]);
  const items = useMemo(() => (data?.items || []).filter((l) => !exclude?.includes(l.id)), [data, exclude]);
  useEffect(() => { onItems?.(data?.origin ? items.map((l) => l.id) : []); }, [data, items, onItems]);

  const choose = (c) => {
    const loc = formatLocation(c.city, c.province);
    localStorage.setItem(pickKey, loc);
    window.dispatchEvent(new CustomEvent(NEAR_CITY_EVENT, { detail: loc }));
    setPicked(loc); setEditing(false); setCityText("");
  };

  if (!data) return null;
  const origin = data.origin;
  const label = origin ? formatLocation(origin.city, origin.province) : "";
  if (!origin && !editing) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="near-you">
        <button onClick={() => setEditing(true)} data-testid="near-you-pick" className="w-full rounded-2xl border-2 border-dashed border-slate-200 p-6 text-left hover:border-[#F5551E] transition-colors">
          <p className="font-display font-bold text-[#12234A] flex items-center gap-2"><Navigation size={18} className="text-[#F5551E]" /> {t("ny_title")}</p>
          <p className="text-sm text-slate-500 mt-1">{t("ny_pick_prompt")}</p>
        </button>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="near-you">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#12234A] flex items-center gap-2">
            <Navigation size={24} className="text-[#F5551E]" /> {t("ny_title")}
          </h2>
          <div className="text-slate-500 mt-1 flex flex-wrap items-center gap-2 text-sm">
            {origin && <select value={radius} onChange={(e) => chooseRadius(e.target.value)} aria-label={t("ny_radius")} data-testid="near-you-radius"
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-[#12234A] focus:outline-none focus:ring-2 focus:ring-[#F5551E]/30 cursor-pointer">
              <option value="province">{t("ny_province")}</option>
              {["25", "50", "100", "250"].map((k) => <option key={k} value={k}>{k} km</option>)}
            </select>}
            {origin && <span data-testid="near-you-origin" className="inline-flex items-center gap-1"><MapPin size={14} /> {data.scope === "province" ? t("ny_in_prov").replace("{prov}", PROVINCE_NAME[origin.province] || origin.province) : t("ny_within").replace("{km}", data.radius_km)} <b className="text-[#12234A]">{label}</b>{origin.source === "approx" && <span className="text-slate-400">({t("ny_approx")})</span>}</span>}
            {editing
              ? <div className="flex items-center gap-1">
                  <div className="w-60"><CityInput autoFocus value={cityText} onChange={setCityText} onPick={choose} testid="near-you-city" placeholder={t("dist_pick_city")} className="h-9 rounded-xl text-sm"
                    onKeyDown={(e) => e.key === "Escape" && setEditing(false)} /></div>
                  <button onClick={() => { setEditing(false); setCityText(""); }} aria-label={t("cityp_dismiss")} data-testid="near-you-cancel" className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
                </div>
              : <button onClick={() => setEditing(true)} data-testid="near-you-change" className="text-[#F5551E] font-semibold hover:underline">{t("ny_change")}</button>}
          </div>
        </div>
        <div className="flex items-center gap-4">
        {origin && (items.length > 1 || cat !== "all") && (
          <div className="inline-flex rounded-xl bg-slate-100 p-1" role="group" aria-label={t("ny_sort")} data-testid="near-you-sort">
            {[["newest", t("ny_newest")], ["closest", t("ny_closest")]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => chooseSort(v)} data-testid={`near-you-sort-${v}`} aria-pressed={sort === v}
                className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${sort === v ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500 hover:text-[#12234A]"}`}>{l}</button>
            ))}
          </div>
        )}
        {origin && items.length > 0 && (
          <Link to={data.scope === "province" ? `/browse?province=${origin.province}` : `/browse?near=${encodeURIComponent(label)}&radius=${Math.min(100, data.radius_km)}`} data-testid="near-you-see-all"
            className="text-[#F5551E] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
            {t("ny_see_all")} <ArrowRight size={18} />
          </Link>
        )}
        </div>
      </div>
      {origin && (
        <div className="-mx-4 px-4 mb-5 flex gap-2 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible" data-testid="near-you-cats">
          {[{ id: "all" }, ...CATEGORIES.filter((c) => !["all", "lost-found"].includes(c.id))].map((c) => (
            <button key={c.id} type="button" onClick={() => setCat(c.id)} data-testid={`near-you-cat-${c.id}`} aria-pressed={cat === c.id}
              className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${cat === c.id ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#F5551E] hover:text-[#F5551E]"}`}>
              {c.id === "all" ? t("ny_all") : (lang === "fr" ? (CATEGORY_LABELS_FR[c.id] || c.label) : c.label)}
            </button>
          ))}
        </div>
      )}
      {origin && items.length === 0
        ? <p className="text-slate-500 text-sm" data-testid="near-you-empty">{t(cat === "all" ? "ny_empty" : "ny_empty_cat")}</p>
        : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{items.map((l) => <ListingCard key={l.id} listing={l} />)}</div>}
    </section>
  );
}
